import React, { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { Redirect } from 'react-router-dom'
import {
  Box,
  Typography,
  Grid2 as Grid,
  TextField,
  MenuItem,
  Checkbox,
  ListItemText,
  Divider,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from '@mui/material'
import RequestQuoteIcon from '@mui/icons-material/RequestQuote'
import PageHeader from '../Common/PageHeader'
import { toast } from 'react-toastify'
import { useAuthentication } from '../../hooks/useAuthentication'
import Loader from '../Loader'
import {
  GET_DEPARTMENTS,
  GET_CATEGORIES,
  GET_FINANCIAL_YEARS,
} from '../Vouchers/gql'
import { GET_BUDGETS, SET_BUDGET, GET_MY_BUDGET_ACCESS } from '../../api/budget'
import { findCurrentFinancialYear } from '../../utils/utils'
import theme from '../../theme'

type BudgetRow = {
  key: string
  label: string
  costCategoryId: string
  subCostCategoryId: string | null
}

const cellKey = (
  departmentId: string,
  costCategoryId: string,
  subCostCategoryId: string | null,
) => `${departmentId}:${costCategoryId}:${subCostCategoryId ?? ''}`

const CLEAR_FILTER = '__CLEAR__'

const formatSek = (amount: number) =>
  new Intl.NumberFormat('en-SE', {
    style: 'currency',
    currency: 'SEK',
    maximumFractionDigits: 0,
  }).format(amount)

const Budgets = () => {
  const { authState } = useAuthentication()

  const myAccessResult = useQuery(GET_MY_BUDGET_ACCESS, {
    context: { headers: { authorization: authState.authToken } },
  })
  const accessLevel = myAccessResult.data?.myBudgetAccess?.level
  const canEdit = !!myAccessResult.data?.myBudgetAccess?.can_edit

  const financialYearsResult = useQuery(GET_FINANCIAL_YEARS, {
    context: { headers: { authorization: authState.authToken } },
  })
  const departmentsResult = useQuery(GET_DEPARTMENTS, {
    context: { headers: { authorization: authState.authToken } },
  })
  const categoriesResult = useQuery(GET_CATEGORIES, {
    context: { headers: { authorization: authState.authToken } },
  })

  const [selectedFinancialYear, setSelectedFinancialYear] = useState<string>('')
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([])
  const [selectedCostCategories, setSelectedCostCategories] = useState<
    string[]
  >([])
  const [isDepartmentFilterOpen, setIsDepartmentFilterOpen] = useState(false)
  const [isCostCategoryFilterOpen, setIsCostCategoryFilterOpen] =
    useState(false)

  useEffect(() => {
    if (selectedFinancialYear || !financialYearsResult.data?.financialYears)
      return
    const current = findCurrentFinancialYear(
      financialYearsResult.data.financialYears,
    )
    setSelectedFinancialYear(
      current
        ? String(current)
        : String(financialYearsResult.data.financialYears[0]?.id ?? ''),
    )
  }, [financialYearsResult.data, selectedFinancialYear])

  // Only 'ALL' ever reaches the table below -- 'NONE' and 'OWN' are redirected
  // away by the guard clauses further down, so there's no point fetching for them.
  const budgetsResult = useQuery(GET_BUDGETS, {
    variables: { financial_year_id: selectedFinancialYear || null },
    skip: !selectedFinancialYear || accessLevel !== 'ALL',
    context: { headers: { authorization: authState.authToken } },
    fetchPolicy: 'network-only',
  })

  const [setBudget] = useMutation(SET_BUDGET)

  // amount per cell, keyed by "departmentId:costCategoryId:subCostCategoryId"
  const [amounts, setAmounts] = useState<Record<string, number>>({})
  // last amount confirmed saved to the backend -- what a Cancel reverts to
  const [savedAmounts, setSavedAmounts] = useState<Record<string, number>>({})
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    const budgets = budgetsResult.data?.budgets ?? []
    const next: Record<string, number> = {}
    budgets.forEach((b: any) => {
      next[
        cellKey(
          b.department.id,
          b.cost_category.id,
          b.sub_cost_category?.id ?? null,
        )
      ] = b.amount
    })
    setAmounts(next)
    setSavedAmounts(next)
    setIsEditing(false)
  }, [budgetsResult.data])

  // Own-department viewers are redirected away before this renders (see the
  // guard clause below), so this is always the full department list.
  const departments = useMemo(
    () =>
      [...(departmentsResult.data?.departments ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    [departmentsResult.data],
  )

  const visibleDepartments = useMemo(
    () =>
      selectedDepartments.length === 0
        ? departments
        : departments.filter((d) => selectedDepartments.includes(d.id)),
    [departments, selectedDepartments],
  )

  const sortedCostCategories = useMemo(
    () =>
      [...(categoriesResult.data?.costCategories ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    [categoriesResult.data],
  )

  const allRows = useMemo<BudgetRow[]>(() => {
    const categories = categoriesResult.data?.costCategories ?? []
    const rows: BudgetRow[] = []
    for (const c of categories) {
      if (c.is_granular) {
        for (const sc of c.sub_cost_categories) {
          rows.push({
            key: `sub:${sc.id}`,
            label: sc.name,
            costCategoryId: c.id,
            subCostCategoryId: sc.id,
          })
        }
      } else {
        rows.push({
          key: `cat:${c.id}`,
          label: c.name,
          costCategoryId: c.id,
          subCostCategoryId: null,
        })
      }
    }
    return rows
  }, [categoriesResult.data])

  const budgetRows = useMemo<BudgetRow[]>(() => {
    const rows =
      selectedCostCategories.length === 0
        ? allRows
        : allRows.filter((row) =>
            selectedCostCategories.includes(row.costCategoryId),
          )
    if (isEditing) return rows
    return rows.filter((row) =>
      visibleDepartments.some(
        (d) =>
          (amounts[cellKey(d.id, row.costCategoryId, row.subCostCategoryId)] ??
            0) > 0,
      ),
    )
  }, [allRows, selectedCostCategories, visibleDepartments, amounts, isEditing])

  const handleChange = (key: string, value: string) => {
    const parsed = value === '' ? 0 : Number(value)
    if (Number.isNaN(parsed)) return
    setAmounts((prev) => ({ ...prev, [key]: parsed }))
  }

  const handleCancel = () => {
    setAmounts(savedAmounts)
    setIsEditing(false)
  }

  const handleSave = async () => {
    const changedKeys = allRows.flatMap((row) =>
      departments
        .map((d) => cellKey(d.id, row.costCategoryId, row.subCostCategoryId))
        .filter((key) => (amounts[key] ?? 0) !== (savedAmounts[key] ?? 0)),
    )

    if (changedKeys.length === 0) {
      setIsEditing(false)
      return
    }

    setIsSaving(true)
    const results = await Promise.allSettled(
      changedKeys.map((key) => {
        const [departmentId, costCategoryId, rawSubCostCategoryId] =
          key.split(':')
        return setBudget({
          variables: {
            input: {
              financial_year_id: Number(selectedFinancialYear),
              department_id: departmentId,
              cost_category_id: costCategoryId,
              sub_cost_category_id: rawSubCostCategoryId || null,
              amount: amounts[key] ?? 0,
            },
          },
        }).then(() => key)
      }),
    )
    setIsSaving(false)

    const succeededKeys = results
      .filter(
        (r): r is PromiseFulfilledResult<string> => r.status === 'fulfilled',
      )
      .map((r) => r.value)
    const failedCount = results.length - succeededKeys.length

    if (succeededKeys.length > 0) {
      setSavedAmounts((prev) => {
        const next = { ...prev }
        succeededKeys.forEach((key) => {
          next[key] = amounts[key] ?? 0
        })
        return next
      })
    }

    if (failedCount === 0) {
      toast.success('Budget saved')
      setIsEditing(false)
    } else {
      toast.error(
        `${failedCount} of ${changedKeys.length} change(s) could not be saved -- fix and try again.`,
      )
      // Revert only the failed cells so the saved ones don't look pending.
      setAmounts((prev) => {
        const next = { ...prev }
        changedKeys
          .filter((key) => !succeededKeys.includes(key))
          .forEach((key) => {
            next[key] = savedAmounts[key] ?? 0
          })
        return next
      })
    }
  }

  const rowTotal = (row: BudgetRow) =>
    visibleDepartments.reduce(
      (sum, d) =>
        sum +
        (amounts[cellKey(d.id, row.costCategoryId, row.subCostCategoryId)] ??
          0),
      0,
    )

  const columnTotal = (departmentId: string) =>
    budgetRows.reduce(
      (sum: number, row) =>
        sum +
        (amounts[
          cellKey(departmentId, row.costCategoryId, row.subCostCategoryId)
        ] ?? 0),
      0,
    )

  const grandTotal = visibleDepartments.reduce(
    (sum, d) => sum + columnTotal(d.id),
    0,
  )

  if (myAccessResult.loading) {
    return <Loader />
  }

  if (accessLevel === 'NONE' || !accessLevel) {
    return <Redirect to="/panel" />
  }

  if (accessLevel === 'OWN') {
    return <Redirect to="/panel/budget-report" />
  }

  if (
    financialYearsResult.loading ||
    departmentsResult.loading ||
    categoriesResult.loading ||
    (!selectedFinancialYear &&
      !!financialYearsResult.data?.financialYears?.length)
  ) {
    return <Loader />
  }

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader title="Budgets" icon={RequestQuoteIcon} />
      {!canEdit && (
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.95rem', mb: 1 }}>
          Read-only — only the National Secretary Maal can change budget
          figures.
        </Typography>
      )}
      <Grid container spacing={2} sx={{ mb: 3, alignItems: 'flex-end' }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Financial Year"
            value={selectedFinancialYear}
            onChange={(e) => setSelectedFinancialYear(e.target.value)}
            disabled={isEditing}
          >
            {financialYearsResult.data?.financialYears?.map((fy: any) => (
              <MenuItem key={fy.id} value={fy.id}>
                {fy.year}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Department"
            value={selectedDepartments}
            onChange={(e) => {
              const value = e.target.value
              const next = typeof value === 'string' ? value.split(',') : value
              if (next.includes(CLEAR_FILTER)) {
                setSelectedDepartments([])
                setIsDepartmentFilterOpen(false)
              } else {
                setSelectedDepartments(next)
              }
            }}
            slotProps={{
              inputLabel: { shrink: true },
              select: {
                multiple: true,
                displayEmpty: true,
                open: isDepartmentFilterOpen,
                onOpen: () => setIsDepartmentFilterOpen(true),
                onClose: () => setIsDepartmentFilterOpen(false),
                renderValue: (selected: unknown) =>
                  (selected as string[]).length === 0
                    ? 'All'
                    : departments
                        .filter((d) => (selected as string[]).includes(d.id))
                        .map((d) => d.name)
                        .join(', '),
              },
            }}
          >
            {selectedDepartments.length > 0 && [
              <MenuItem key="clear" value={CLEAR_FILTER}>
                <em>Clear Selection</em>
              </MenuItem>,
              <Divider key="clear-divider" />,
            ]}
            {departments.map((d) => (
              <MenuItem key={d.id} value={d.id}>
                <Checkbox
                  size="small"
                  checked={selectedDepartments.includes(d.id)}
                />
                <ListItemText primary={d.name} />
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Cost Category"
            value={selectedCostCategories}
            onChange={(e) => {
              const value = e.target.value
              const next = typeof value === 'string' ? value.split(',') : value
              if (next.includes(CLEAR_FILTER)) {
                setSelectedCostCategories([])
                setIsCostCategoryFilterOpen(false)
              } else {
                setSelectedCostCategories(next)
              }
            }}
            slotProps={{
              inputLabel: { shrink: true },
              select: {
                multiple: true,
                displayEmpty: true,
                open: isCostCategoryFilterOpen,
                onOpen: () => setIsCostCategoryFilterOpen(true),
                onClose: () => setIsCostCategoryFilterOpen(false),
                renderValue: (selected: unknown) =>
                  (selected as string[]).length === 0
                    ? 'All'
                    : sortedCostCategories
                        .filter((c) => (selected as string[]).includes(c.id))
                        .map((c) => c.name)
                        .join(', '),
              },
            }}
          >
            {selectedCostCategories.length > 0 && [
              <MenuItem key="clear" value={CLEAR_FILTER}>
                <em>Clear Selection</em>
              </MenuItem>,
              <Divider key="clear-divider" />,
            ]}
            {sortedCostCategories.map((c: any) => (
              <MenuItem key={c.id} value={c.id}>
                <Checkbox
                  size="small"
                  checked={selectedCostCategories.includes(c.id)}
                />
                <ListItemText primary={c.name} />
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        {canEdit && (
          <Grid size={{ xs: 12, sm: 'auto' }}>
            {isEditing ? (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving…' : 'Save'}
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
              </Box>
            ) : (
              <Button variant="outlined" onClick={() => setIsEditing(true)}>
                Edit
              </Button>
            )}
          </Grid>
        )}
      </Grid>

      {budgetsResult.loading ? (
        <Loader />
      ) : (
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    position: 'sticky',
                    left: 0,
                    bgcolor: 'grey.100',
                    zIndex: 1,
                    minWidth: 180,
                    py: 1.5,
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    color: 'text.secondary',
                  }}
                >
                  Cost Category
                </TableCell>
                {visibleDepartments.map((d) => (
                  <TableCell
                    key={d.id}
                    sx={{
                      minWidth: 130,
                      bgcolor: 'grey.100',
                      py: 1.5,
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      color: 'text.secondary',
                    }}
                  >
                    {d.name}
                  </TableCell>
                ))}
                <TableCell
                  sx={{
                    minWidth: 130,
                    bgcolor: 'grey.100',
                    py: 1.5,
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    color: 'text.secondary',
                  }}
                >
                  Total
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {budgetRows.map((row) => (
                <TableRow key={row.key}>
                  <TableCell
                    sx={{
                      position: 'sticky',
                      left: 0,
                      bgcolor: 'background.paper',
                      zIndex: 1,
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      py: 1.5,
                    }}
                  >
                    {row.label}
                  </TableCell>
                  {visibleDepartments.map((d) => {
                    const key = cellKey(
                      d.id,
                      row.costCategoryId,
                      row.subCostCategoryId,
                    )
                    return (
                      <TableCell key={key} sx={{ fontSize: '0.95rem', py: 1.5 }}>
                        {canEdit && isEditing ? (
                          <input
                            type="number"
                            value={amounts[key] ?? ''}
                            disabled={isSaving}
                            onChange={(e) => handleChange(key, e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') e.currentTarget.blur()
                            }}
                            style={{
                              width: 110,
                              padding: '8px 10px',
                              fontSize: '0.95rem',
                              fontFamily: 'inherit',
                              color: 'inherit',
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: 4,
                              opacity: isSaving ? 0.5 : 1,
                            }}
                          />
                        ) : amounts[key] ? (
                          formatSek(amounts[key])
                        ) : (
                          ''
                        )}
                      </TableCell>
                    )
                  })}
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.95rem', py: 1.5 }}>
                    {formatSek(rowTotal(row))}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell
                  sx={{
                    position: 'sticky',
                    left: 0,
                    bgcolor: 'background.paper',
                    zIndex: 1,
                    fontWeight: 700,
                    fontSize: '0.98rem',
                    py: 1.5,
                  }}
                >
                  Total
                </TableCell>
                {visibleDepartments.map((d) => (
                  <TableCell key={d.id} sx={{ fontWeight: 700, fontSize: '0.98rem', py: 1.5 }}>
                    {formatSek(columnTotal(d.id))}
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 700, fontSize: '0.98rem', py: 1.5 }}>
                  {formatSek(grandTotal)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  )
}

export default Budgets
